import { Box, FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import { SubmitButton } from '../shared/SubmitButton';
import type { SurgeonEntity } from '@peritasai/repository';
import { useCreateSurgeon, useUpdateSurgeon } from '@peritasai/repository';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';

export { DrawerMode as SurgeonDrawerMode };

interface SurgeonDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedSurgeon: SurgeonEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  fullName: string;
  npiNumber: string;
  specialty: string;
  providerId: string;
  active: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  npiNumber?: string;
  specialty?: string;
  apiError?: string;
}

const emptyForm: FormState = {
  firstName: '',
  lastName: '',
  fullName: '',
  npiNumber: '',
  specialty: '',
  providerId: '',
  active: true,
};

function validate(
  form: FormState,
  mode: DrawerMode,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (!form.firstName.trim()) {
    errors.firstName = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.surgeonDrawer.firstNameLabel'),
    });
  }
  if (!form.lastName.trim()) {
    errors.lastName = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.surgeonDrawer.lastNameLabel'),
    });
  }
  if (!form.fullName.trim()) {
    errors.fullName = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.surgeonDrawer.fullNameLabel'),
    });
  }
  if (mode === DrawerMode.Create) {
    if (!form.npiNumber.trim()) {
      errors.npiNumber = t('pages.entityManagement.drawer.fieldRequired', {
        fieldName: t('pages.entityManagement.surgeonDrawer.npiNumberLabel'),
      });
    } else if (form.npiNumber.trim().length !== 10) {
      errors.npiNumber = t('pages.entityManagement.surgeonDrawer.npiNumberExactDigits');
    }
  }
  if (!form.specialty.trim()) {
    errors.specialty = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.surgeonDrawer.specialtyLabel'),
    });
  }
  return errors;
}

export const SurgeonDrawer = ({
  open,
  mode,
  selectedSurgeon,
  onClose,
  onSuccess,
}: SurgeonDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const createMutation = useCreateSurgeon();
  const updateMutation = useUpdateSurgeon();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const entityName = t('pages.entityManagement.surgeonDrawer.entityName');

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedSurgeon) {
      setForm({
        firstName: selectedSurgeon.firstName ?? '',
        lastName: selectedSurgeon.lastName ?? '',
        fullName: selectedSurgeon.fullName ?? '',
        npiNumber: selectedSurgeon.npiNumber ?? '',
        specialty: selectedSurgeon.specialty ?? '',
        providerId: selectedSurgeon.providerId ?? '',
        active: selectedSurgeon.active,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedSurgeon]);

  const handleChange =
    (field: keyof Omit<FormState, 'active'>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      if (touched) {
        setErrors(validate(next, mode, t));
      }
    };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, active: e.target.checked }));
  };

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(form, mode, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      if (mode === DrawerMode.Create) {
        const createProviderIdNum = parseInt(form.providerId.trim(), 10);
        await createMutation.mutateAsync({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          fullName: form.fullName.trim(),
          npiNumber: form.npiNumber.trim(),
          specialty: form.specialty.trim(),
          ...(form.providerId.trim().length > 0 && !isNaN(createProviderIdNum) ? { providerId: createProviderIdNum } : {}),
          isActive: form.active,
        });
        setForm(emptyForm);
        setTouched(false);
        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedSurgeon) {
        const updateProviderIdNum = parseInt(form.providerId.trim(), 10);
        await updateMutation.mutateAsync({
          id: selectedSurgeon.id,
          dto: {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            fullName: form.fullName.trim(),
            specialty: form.specialty.trim(),
            providerId: form.providerId.trim().length > 0 && !isNaN(updateProviderIdNum) ? updateProviderIdNum : null,
            isActive: form.active,
          },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  return (
    <SideDrawer
      open={open}
      title={
        mode === DrawerMode.Create
          ? t('pages.entityManagement.drawer.titleCreate', { entityName })
          : t('pages.entityManagement.drawer.titleEdit', { entityName })
      }
      onClose={onClose}
      footer={
        <SubmitButton disabled={isPending} onClick={() => void handleSubmit()}>
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </SubmitButton>
      }
    >
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.surgeonDrawer.firstNameLabel')} *`}
            fullWidth
            size="small"
            value={form.firstName}
            onChange={handleChange('firstName')}
            error={touched && !!errors.firstName}
            helperText={touched ? errors.firstName : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.surgeonDrawer.lastNameLabel')} *`}
            fullWidth
            size="small"
            value={form.lastName}
            onChange={handleChange('lastName')}
            error={touched && !!errors.lastName}
            helperText={touched ? errors.lastName : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.surgeonDrawer.fullNameLabel')} *`}
            fullWidth
            size="small"
            value={form.fullName}
            onChange={handleChange('fullName')}
            error={touched && !!errors.fullName}
            helperText={touched ? errors.fullName : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.surgeonDrawer.npiNumberLabel')} *`}
            fullWidth
            size="small"
            value={form.npiNumber}
            onChange={(e) => {
              const next = { ...form, npiNumber: e.target.value.replace(/\D/g, '').slice(0, 10) };
              setForm(next);
              if (touched) setErrors(validate(next, mode, t));
            }}
            disabled={mode === DrawerMode.Edit}
            inputMode="numeric"
            error={touched && !!errors.npiNumber}
            helperText={touched ? errors.npiNumber : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.surgeonDrawer.specialtyLabel')} *`}
            fullWidth
            size="small"
            value={form.specialty}
            onChange={handleChange('specialty')}
            error={touched && !!errors.specialty}
            helperText={touched ? errors.specialty : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.surgeonDrawer.providerIdLabel')}
            fullWidth
            size="small"
            value={form.providerId}
            onChange={(e) => {
              const next = { ...form, providerId: e.target.value.replace(/\D/g, '').slice(0, 9) };
              setForm(next);
              if (touched) setErrors(validate(next, mode, t));
            }}
            inputMode="numeric"
          />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            label={t('pages.entityManagement.surgeonDrawer.activeLabel')}
            control={
              <Switch
                checked={form.active}
                onChange={handleActiveChange}
                color="primary"
              />
            }
          />
        </Grid>
      </Grid>

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
